import { supabase } from "./supabaseClient";
import { marketingAnonymousId } from "./marketingAttribution";
import { readRestaurantInviteSession, restaurantInviteEventMetadata } from "./restaurantInviteAccess";

const SESSION_KEY = "venaqqi_validation_session_v1";
const INSTALL_FLAG = "__venaqqiProductAnalyticsInstalled";
const RETURN_THRESHOLD_MS = 15_000;

const browserWindow = () => typeof window !== "undefined" ? window : undefined;
const browserDocument = () => typeof document !== "undefined" ? document : undefined;

const randomId = () => {
  const win = browserWindow();
  if (win?.crypto?.randomUUID) return win.crypto.randomUUID();
  return `vq-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
};

const sessionId = () => {
  const win = browserWindow();
  if (!win?.sessionStorage) return randomId();
  try {
    const existing = win.sessionStorage.getItem(SESSION_KEY);
    if (existing?.length >= 8) return existing;
    const created = randomId();
    win.sessionStorage.setItem(SESSION_KEY, created);
    return created;
  } catch {
    return randomId();
  }
};

const clean = (value, max = 120) => {
  if (typeof value !== "string") return null;
  const normalized = value.trim().replace(/\s+/g, " ");
  return normalized ? normalized.slice(0, max) : null;
};

const elementMetadata = (element) => {
  if (!element || typeof element.getAttribute !== "function") return {};
  return {
    tag: clean(element.tagName?.toLowerCase(), 30),
    id: clean(element.id, 80),
    role: clean(element.getAttribute("role"), 40),
    aria_label: clean(element.getAttribute("aria-label"), 100),
    title: clean(element.getAttribute("title"), 100),
    name: clean(element.getAttribute("name"), 80),
    type: clean(element.getAttribute("type"), 40),
    analytics_action: clean(element.getAttribute("data-analytics-action"), 100),
    classes: clean(Array.from(element.classList || []).slice(0, 5).join(" "), 160),
  };
};

const errorMetadata = (source, errorLike) => ({
  source: clean(source, 40) || "client",
  error_name: clean(errorLike?.name || errorLike?.constructor?.name || "Error", 80) || "Error",
  severity: "error",
});

export const trackProductEvent = async (eventName, metadata = {}) => {
  const win = browserWindow();
  if (!win || !eventName) return;

  const inviteSession = readRestaurantInviteSession();
  const inviteMetadata = restaurantInviteEventMetadata(inviteSession);
  const explicitMetadata = metadata && typeof metadata === "object" ? metadata : {};

  const row = {
    event_name: eventName,
    anonymous_id: marketingAnonymousId(),
    session_id: sessionId(),
    path: clean(win.location?.pathname || "/", 300),
    metadata: { ...inviteMetadata, ...explicitMetadata },
  };

  try {
    await supabase.from("product_events").insert(row);
  } catch {
    // Validation analytics must never block or alter product use.
  }
};

export const installProductAnalytics = () => {
  const win = browserWindow();
  const doc = browserDocument();
  if (!win || !doc || win[INSTALL_FLAG]) return;
  win[INSTALL_FLAG] = true;
  let hiddenAt = null;

  void trackProductEvent("app_open", {
    visibility: doc.visibilityState || null,
  });

  doc.addEventListener("click", (event) => {
    const target = event.target?.closest?.("button,a,[role='button'],[data-analytics-action]");
    if (!target) return;
    void trackProductEvent("ui_click", elementMetadata(target));
  }, true);

  doc.addEventListener("submit", (event) => {
    void trackProductEvent("form_submit", elementMetadata(event.target));
  }, true);

  doc.addEventListener("focusin", (event) => {
    const target = event.target;
    if (!target?.matches?.("input,select,textarea")) return;
    void trackProductEvent("input_focus", elementMetadata(target));
  }, true);

  doc.addEventListener("visibilitychange", () => {
    const state = clean(doc.visibilityState, 30);
    void trackProductEvent("visibility_changed", { state });
    if (doc.visibilityState === "hidden") {
      hiddenAt = Date.now();
      return;
    }
    if (doc.visibilityState === "visible" && hiddenAt) {
      const awayMs = Date.now() - hiddenAt;
      hiddenAt = null;
      if (awayMs >= RETURN_THRESHOLD_MS) {
        void trackProductEvent("app_return", {
          away_seconds: Math.min(86_400, Math.max(15, Math.round(awayMs / 1000))),
        });
      }
    }
  });

  win.addEventListener("error", (event) => {
    void trackProductEvent("client_error", errorMetadata("window_error", event?.error));
  });
  win.addEventListener("unhandledrejection", (event) => {
    void trackProductEvent("client_error", errorMetadata("unhandled_rejection", event?.reason));
  });
};
